import type { ClientCartItem } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { getConfig } from '@shared/config'
import { DeliveryMethod, ProductType } from '@shared/gql/document-nodes'
import { DeleteOutline24, minWidthFromTheme } from '@sok/design-system'
import { ListItemCategoryTitle } from 'components/CategoryTitle'
import { ProductCard } from 'components/Product'
import { RemoveCartItem } from 'components/Product/RemoveCartItem'
import { CartOrderHistory } from 'components/ShoppingCart/cart-order-history/CartOrderHistory'
import { CartIsEmpty } from 'components/ShoppingCart/CartIsEmpty'
import { getSortedCategoryNames, groupByCategory, sortInCategories } from 'domain/group-by-category'
import { Comparators } from 'domain/sort-by'
import { Fragment, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRemoveCartItems } from 'services/ClientCart/mutations/use-remove-cart-items'
import { useProductAvailabilities } from 'services/Product/hooks/use-product-availabilities'
import { getProductAvailabilityForDate } from 'services/Product/utils/get-product-availability-for-date'
import styled from 'styled-components'
import { filterAlcoholItems } from 'utils/cart/filter-alcohol-items'
import { filterPurchaseableCartItems, getMissingItems } from 'utils/cart/filter-missing-items'
import { mapCartItemToProduct } from 'utils/cart/partition-cart-items'
import { useGetClientCartItemsByType } from 'utils/cart/use-get-client-cart-items-by-type'
import { useIsLoggedIn } from 'utils/hooks/account/use-is-logged-in'
import { useGetCartAlcoholItems } from 'utils/hooks/cart'
import { useDeliveryDate } from 'utils/hooks/delivery/use-delivery-date'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { uniqueByEan } from 'utils/products/unique-by-ean'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { useGetInitialDeliverySelections } from 'views/delivery-modal/method-and-store/use-get-initial-delivery-selections'
import { CreateListFromCart } from 'views/shoppinglists/CreateListFromCart'

import { SummaryCartItem } from '../components/SummaryCartItem'
import { CartAlertsContainer } from './CartAlertsContainer'
import { CartHeaderContainer } from './CartHeaderContainer'

const { featureFlags } = getConfig()

interface CartSummaryContainerProps {
  className?: string
  'data-test-id'?: string
}

const _CartSummaryContainer: FC<CartSummaryContainerProps> = ({
  className,
  'data-test-id': dataTestId,
}) => {
  const { t } = useTranslation()
  const isAuthenticated = useIsLoggedIn()
  const removeCartItems = useRemoveCartItems()

  const cartItems = useGetClientCartItemsByType(ProductType.Product)
  const { selectedStoreId } = useStoreId()

  const alcoholItems = useGetCartAlcoholItems()

  const { initialDeliveryMethod } = useGetInitialDeliverySelections()

  const isHomeDelivery = initialDeliveryMethod === DeliveryMethod.HomeDelivery
  const itemsInStore = isHomeDelivery
    ? filterAlcoholItems(filterPurchaseableCartItems(cartItems))
    : filterPurchaseableCartItems(cartItems)

  const availabilitiesById = useProductAvailabilities(itemsInStore)
  const availabilityDate = useDeliveryDate()
  const getCurrentProductAvailabilityLabel = (id: string) =>
    getProductAvailabilityForDate(availabilitiesById?.[id], availabilityDate)?.label

  const cartItemsBasedOnCategory = sortInCategories(
    groupByCategory(itemsInStore, (x) => x.mainCategoryName),
    [(a, b) => Comparators.gte(a.name, b.name)],
  )
  const missingItems = getMissingItems(cartItems)

  const missingAndAlcoholItems = uniqueByEan([...missingItems, ...alcoholItems])
  const itemsLiftedToTop = isHomeDelivery ? missingAndAlcoholItems : missingItems

  const sortedCategories = getSortedCategoryNames(cartItemsBasedOnCategory)

  /**
   * for tracking purposes
   * form array of ids for getting actual visible list indexes for product items, as they are
   * sorted by category in the list
   */
  const trackingCartItemIndexes = [
    ...itemsLiftedToTop.map((x) => x.id),
    ...sortedCategories
      .map((x) => cartItemsBasedOnCategory[x])
      .flat()
      .map((x) => x.id),
  ]

  const handleRemoveCartItem = (id: ClientCartItem['id']) => () => removeCartItems([{ id }])

  if (!cartItems.length && isAuthenticated) {
    return (
      <div className={className} data-test-id="shopping-cart-summary">
        <h1>{t('Shopping cart')}</h1>
        <TrackingContextProvider
          trackingContext={TrackingContext.COPY_PREVIOUS_ORDER}
          listName={'copy-previous-order'}
        >
          <CartOrderHistory />
        </TrackingContextProvider>
      </div>
    )
  }

  if (cartItems.length === 0) {
    return (
      <CartIsEmptyContainer>
        <CartIsEmpty />
      </CartIsEmptyContainer>
    )
  }

  return (
    <div className={className} data-test-id="shopping-cart-summary">
      <h1>{t('Shopping cart')}</h1>
      <ListFromCartButtonContainer>
        {featureFlags.shoppingLists && isAuthenticated ? (
          <CreateListFromCart dataTestId="create-new-list-from-cart-desktop" />
        ) : null}
      </ListFromCartButtonContainer>
      <CartHeaderContainer cartItems={cartItems} />
      <CartAlertsContainer cartItems={cartItems} />
      <ShoppingCart data-test-id={dataTestId}>
        {/* This Displays the missing cart Items first on the List */}
        {itemsLiftedToTop.map((cartItem: ClientCartItem) => {
          return (
            <TrackingContextProvider
              key={cartItem.id}
              listPosition={trackingCartItemIndexes.indexOf(cartItem.id)}
              availabilityLabel={getCurrentProductAvailabilityLabel(cartItem.id)}
            >
              <ProductCard.Cart.Success
                disabled={!cartItem.inStoreSelection}
                data={mapCartItemToProduct(cartItem, selectedStoreId)}
                data-product-id={cartItem.id}
                data-test-id="product-card__cartSummary-missingItems"
                readOnlyName
                readOnlyImage
              >
                <RemoveCartItem
                  icon={<DeleteOutline24 />}
                  text={t('Remove')}
                  onClick={handleRemoveCartItem(cartItem.id)}
                />
              </ProductCard.Cart.Success>
            </TrackingContextProvider>
          )
        })}
        {/* This Groups the Cart Items with their category title */}
        {sortedCategories.map((category) => (
          <Fragment key={category}>
            <ListItemCategoryTitle>{category}</ListItemCategoryTitle>
            {cartItemsBasedOnCategory[category].map((cartItem) => (
              <TrackingContextProvider
                key={cartItem.id}
                listPosition={trackingCartItemIndexes.indexOf(cartItem.id)}
                availabilityLabel={getCurrentProductAvailabilityLabel(cartItem.id)}
              >
                <SummaryCartItem
                  cartItem={cartItem}
                  availabilities={availabilitiesById?.[cartItem.id]}
                />
              </TrackingContextProvider>
            ))}
          </Fragment>
        ))}
        {featureFlags.shoppingLists && isAuthenticated ? (
          <MobileCreateListFromCart dataTestId="create-new-list-from-cart-mobile" />
        ) : null}
      </ShoppingCart>
    </div>
  )
}

_CartSummaryContainer.displayName = 'CartSummaryContainer'

const MobileCreateListFromCart = styled(CreateListFromCart)({})
const ListFromCartButtonContainer = styled.div({})

export const CartSummaryContainer = styled(memo(_CartSummaryContainer))(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.color.white,
  paddingBottom: theme.spacings.medium,
  position: 'relative',
  listStyleType: 'none',
  [minWidthFromTheme(theme).desktop]: {
    minHeight: '80vh',
  },

  '> h1': {
    ...theme.variants.title1,
    marginTop: theme.spacings.large,
    padding: theme.spacings.medium,
    paddingBottom: 0,
    width: '100%',
  },

  [ListItemCategoryTitle]: {
    paddingLeft: theme.spacings.medium,
  },

  [ListFromCartButtonContainer]: {
    display: 'none',
    [minWidthFromTheme(theme).desktop]: {
      display: 'flex',
      flexDirection: 'row',
      width: 'auto',
    },
  },

  [MobileCreateListFromCart]: {
    [minWidthFromTheme(theme).desktop]: {
      display: 'none',
    },
  },
}))

const ShoppingCart = styled.div(
  {
    alignContent: 'flex-start',
    flexGrow: 1,
    flexWrap: 'wrap',
  },
  ({ theme }) => ({
    paddingBottom: theme.spacings.medium,
  }),
)

const CartIsEmptyContainer = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  /** global nav + local nav + just a bit to show the footer */
  minHeight: `calc(100vh - ${60 + 80 + 128}px)`,
  padding: theme.spacings.xLarge,
}))
